import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Plus, X, MousePointerClick } from "lucide-react";

function Line({ line, lineIndex, isActive, isRecent, onWordClick, onPhraseSelect, onLineClick }) {
  const tokens = line.text.replace(/\n/g, " ").split(/(\s+)/);

  const handleMouseUp = () => {
    const sel = window.getSelection();
    const text = sel?.toString().trim().replace(/\s+/g, " ");
    if (text && text.length > 1 && text.length < 100) {
      const cleaned = text.replace(/^[^\w']+|[^\w']+$/g, "");
      if (cleaned) onPhraseSelect(lineIndex, cleaned);
    }
  };

  return (
    <div
      onClick={() => onLineClick(line.start)}
      onMouseUp={handleMouseUp}
      data-testid={`subtitle-line-${Math.floor(line.start)}`}
      className={cn(
        "rounded-xl cursor-pointer transition-all duration-500 ease-out select-text mx-1 my-1",
        isActive
          ? "bg-card border border-primary/30 shadow-md px-4 py-3.5"
          : isRecent
          ? "bg-muted/40 px-4 py-2 opacity-70"
          : "px-4 py-1.5 text-muted-foreground hover:bg-accent/40"
      )}
    >
      <p className={cn("leading-relaxed", isActive ? "text-lg font-medium text-foreground" : "text-sm")}>
        {tokens.map((token, i) => {
          if (/^\s+$/.test(token)) return token;
          const cleanWord = token.replace(/[^a-zA-Z']/g, "");
          if (!cleanWord) return <span key={i}>{token}</span>;
          return (
            <span
              key={i}
              onClick={(e) => {
                e.stopPropagation();
                const sel = window.getSelection();
                if (sel && sel.toString().trim().length > 0) return; // a drag phrase selection is active
                onWordClick(lineIndex, cleanWord);
              }}
              data-testid={`subtitle-word-${cleanWord.toLowerCase()}`}
              className="rounded px-0.5 transition-colors duration-150 hover:bg-yellow-200 dark:hover:bg-yellow-900/40"
            >
              {token}
            </span>
          );
        })}
      </p>
    </div>
  );
}

export default function Subtitles({ transcript, currentTime, onWordClick, onSeek }) {
  const containerRef = useRef(null);
  const activeLineRef = useRef(null);
  const [pendingPhrase, setPendingPhrase] = useState(null); // { lineIndex, text }

  const activeIndex = transcript.findIndex(
    (line, i) =>
      currentTime >= line.start &&
      (i === transcript.length - 1 || currentTime < transcript[i + 1].start)
  );

  // Only auto-scroll when the active line has actually drifted out of view, instead of
  // re-centering on every single transition (which felt like the whole list was "sliding").
  useEffect(() => {
    const container = containerRef.current;
    const activeEl = activeLineRef.current;
    if (!container || !activeEl) return;
    const containerRect = container.getBoundingClientRect();
    const activeRect = activeEl.getBoundingClientRect();
    const isVisible = activeRect.top >= containerRect.top + 8 && activeRect.bottom <= containerRect.bottom - 8;
    if (!isVisible) {
      activeEl.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [activeIndex]);

  const buildContext = (lineIndex) => {
    const prev = transcript[lineIndex - 1]?.text || "";
    const current = transcript[lineIndex].text;
    const next = transcript[lineIndex + 1]?.text || "";
    return [prev, current, next].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
  };

  const handleWordClick = (lineIndex, word) => {
    onWordClick(word, buildContext(lineIndex), transcript[lineIndex].start);
  };

  const handlePhraseSelect = (lineIndex, text) => {
    setPendingPhrase({ lineIndex, text });
  };

  const confirmPhrase = () => {
    if (!pendingPhrase) return;
    onWordClick(pendingPhrase.text, buildContext(pendingPhrase.lineIndex), transcript[pendingPhrase.lineIndex].start);
    setPendingPhrase(null);
    window.getSelection()?.removeAllRanges();
  };

  const cancelPhrase = () => {
    setPendingPhrase(null);
    window.getSelection()?.removeAllRanges();
  };

  if (!transcript || transcript.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-center text-muted-foreground p-8" data-testid="no-subtitles-message">
        Субтитры для этого видео недоступны
      </div>
    );
  }

  return (
    <div className="relative h-full flex flex-col">
      <div className="flex items-center gap-1.5 px-3 pt-2 text-xs text-muted-foreground shrink-0" data-testid="subtitle-selection-hint">
        <MousePointerClick className="w-3.5 h-3.5" />
        Клик по слову — добавить · Выделите мышкой несколько слов — добавить словосочетание
      </div>
      <div ref={containerRef} className="flex-1 overflow-y-auto px-2 py-3" data-testid="subtitles-container">
        {transcript.map((line, i) => (
          <div key={i} ref={i === activeIndex ? activeLineRef : null}>
            <Line
              line={line}
              lineIndex={i}
              isActive={i === activeIndex}
              isRecent={i === activeIndex - 1}
              onWordClick={handleWordClick}
              onPhraseSelect={handlePhraseSelect}
              onLineClick={onSeek}
            />
          </div>
        ))}
      </div>

      {pendingPhrase && (
        <div
          className="absolute bottom-16 left-3 right-20 flex items-center justify-between gap-2 bg-primary text-primary-foreground rounded-lg px-3 py-2 shadow-lg animate-in fade-in slide-in-from-bottom-2 z-10"
          data-testid="phrase-selection-bar"
        >
          <span className="text-sm font-medium truncate">«{pendingPhrase.text}»</span>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={confirmPhrase}
              className="p-1.5 rounded-md bg-primary-foreground/20 hover:bg-primary-foreground/30 transition-colors"
              data-testid="phrase-selection-confirm-button"
            >
              <Plus className="w-4 h-4" />
            </button>
            <button
              onClick={cancelPhrase}
              className="p-1.5 rounded-md hover:bg-primary-foreground/20 transition-colors"
              data-testid="phrase-selection-cancel-button"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
